import { useEffect, useMemo, useState } from 'react';
import { useArcadeStoreState } from './useArcadeStoreState';
import {
  CreateEventResponse,
  EventStatus,
  EventType,
  getEvent,
  Network,
  RecordWithPlaintext,
  requestCreateEvent,
} from '@puzzlehq/sdk';
import { getRecordPartBalance } from '@puzzlehq/frontend-utils/src/utils';
import { useMutation, useQueries } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { trpc } from '@src/data/trpc';

type PartJoinHelperProps = {
  price?: number;
};

const MAX_JOIN_COUNT = 5;
const network: Network = import.meta.env.VITE_NETWORK!;

export const usePartJoinHelper = ({ price }: PartJoinHelperProps) => {
  const { descendingPartRecords } = useArcadeStoreState();

  const { result, recordCount } = useMemo(() => {
    if (descendingPartRecords === undefined || price === undefined) {
      return {
        recordsToJoin: undefined,
        recordCount: 0,
      };
    }

    const result: RecordWithPlaintext[][] = [];
    let remainingPrice = price;
    let index = 0;
    let recordCount = 0;

    while (remainingPrice > 0 && index < descendingPartRecords.length) {
      const recordsToJoin = descendingPartRecords.slice(
        index,
        index + MAX_JOIN_COUNT
      );
      const sumOfRecords = recordsToJoin.reduce(
        (sum, record) => sum + getRecordPartBalance(record),
        0
      );

      recordCount += recordsToJoin.length;

      if (sumOfRecords >= remainingPrice) {
        // If the sum of selected records meets or exceeds the price
        result.push(recordsToJoin);
        console.log('Sufficient coins selected:', recordsToJoin);
        break;
      } else {
        // Join the records and continue
        result.push(recordsToJoin);
        remainingPrice -= sumOfRecords;
        index += MAX_JOIN_COUNT;
      }
    }

    return { result, recordCount };
  }, [JSON.stringify(descendingPartRecords), price]);

  return {
    recordsToJoin: result,
    recordCount,
  };
};

type PartJoinProps = {
  recordsToJoin: RecordWithPlaintext[][];
  fees: number[];
};

const partJoinFunctionsTestnet = [
  ['puzzle_arcade_ticket_v001.aleo', 'join'],
  ['puzzle_arcade_ticket_v001.aleo', 'join2'],
  ['puzzle_arcade_ticket_v001.aleo', 'join3'],
  ['puzzle_arcade_ticket_v001.aleo', 'join4'],
  ['puzzle_arcade_ticket_v001.aleo', 'join5'],
  ['par_ticket_joins_v1.aleo', 'join6'],
  ['par_ticket_joins_v1.aleo', 'join7'],
  ['par_ticket_joins_v1.aleo', 'join8'],
  ['par_ticket_joins_v1.aleo', 'join9'],
  ['par_ticket_joins_v1.aleo', 'join10'],
  ['par_ticket_joins_v1.aleo', 'join11'],
  // ['par_ticket_joins_v1.aleo', 'join12'],
  // ['par_ticket_joins_v1.aleo', 'join13'],
];

const partJoinFunctionsMainnet = [
  ['puzzle_arcade_ticket_v002.aleo', 'join'],
  ['puzzle_arcade_ticket_v002.aleo', 'join2'],
  ['puzzle_arcade_ticket_v002.aleo', 'join3'],
  ['puzzle_arcade_ticket_v002.aleo', 'join4'],
  ['puzzle_arcade_ticket_v002.aleo', 'join5'],
  ['par_ticket_joins_v1.aleo', 'join6'],
  ['par_ticket_joins_v1.aleo', 'join7'],
  ['par_ticket_joins_v1.aleo', 'join8'],
  ['par_ticket_joins_v1.aleo', 'join9'],
  ['par_ticket_joins_v1.aleo', 'join10'],
  ['par_ticket_joins_v1.aleo', 'join11'],
  // ['par_ticket_joins_v1.aleo', 'join12'],
  // ['par_ticket_joins_v1.aleo', 'join13'],
];

const useJoinFees = () => {
  const partJoinFunctions =
    network === Network.AleoMainnet
      ? partJoinFunctionsMainnet
      : partJoinFunctionsTestnet;

  const queries = useQueries({
    queries: partJoinFunctions.map(([programId, functionId]) => ({
      queryKey: ['fee', programId, functionId],
      queryFn: async () => {
        return await trpc.getFee.query({
          network: network!,
          programId,
          functionId,
        });
      },
      staleTime: Infinity,
      cacheTime: Infinity,
    })),
  });

  const fees = useMemo(() => {
    return queries.map((query) => {
      if (query.data?.fee.total) return query.data.fee.total;
      return 0.1;
    });
  }, [queries]);

  return fees;
};

export const joinParts = async ({ recordsToJoin, fees }: PartJoinProps) => {
  const responses = await Promise.all(
    recordsToJoin.map(async (records) => {
      const recordCount = records.length;
      if (recordCount > MAX_JOIN_COUNT)
        throw new Error('recordCount > MAX_JOIN_COUNT');

      const partJoinFunctions =
        network === Network.AleoMainnet
          ? partJoinFunctionsMainnet
          : partJoinFunctionsTestnet;
      const programId = partJoinFunctions[recordCount - 1][0];
      const functionId = partJoinFunctions[recordCount - 1][1];

      return await requestCreateEvent({
        fee: fees[records.length - 1] ?? 0.1,
        functionId,
        programId,
        inputs: records.map((r) => r.plaintext),
        type: EventType.Execute,
      });
    })
  );

  console.log('joinParts responses', responses);
  return responses;
};

type UsePartJoinProps = {
  recordsToJoin?: RecordWithPlaintext[][];
};
export const useJoinParts = ({ recordsToJoin }: UsePartJoinProps) => {
  const fees = useJoinFees();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!recordsToJoin) throw new Error('recordsToJoin is undefined!');
      if (recordsToJoin.length === 0)
        throw new Error('recordsToJoin.length === 0');

      return await joinParts({
        recordsToJoin,
        fees: fees,
      });
    },
  });

  return mutation;
};

type UsePartJoinStatusProps = {
  requests?: CreateEventResponse[];
  isLoadingJoinParts?: boolean;
};
export type JoinStatus =
  | 'Idle'
  | 'Joining'
  | 'Error'
  | 'PartialSuccess'
  | 'Success';
export const useJoinPartsStatus = ({
  requests,
  isLoadingJoinParts,
}: UsePartJoinStatusProps) => {
  const [joinStatus, setJoinStatus] = useState<JoinStatus>('Idle');
  const [error, setError] = useState<string | undefined>();
  const [successfulCount, setSuccessfulCount] = useState<number>(0);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [eventStatuses, setEventStatuses] = useState<
    EventStatus[] | undefined
  >();

  useEffect(() => {
    if (isLoadingJoinParts && joinStatus === 'Idle') {
      setJoinStatus('Joining');
    }
  }, [isLoadingJoinParts]);

  const queries =
    requests
      ?.filter((request) => !!request.eventId)
      .map((request) => ({
        queryKey: ['getEventJoin', request.eventId!],
        queryFn: async () => {
          return await getEvent({ id: request.eventId! });
        },
        staleTime: 5_000,
        refetchInterval: 7_500,
      })) ?? [];

  const queriesRes = useQueries({
    queries: queries,
  });

  useEffect(() => {
    if (!requests) return;
    if (joinStatus === 'Error') {
      toast.error(error ?? 'Error joining PART');
    } else if (['PartialSuccess', 'Success'].includes(joinStatus)) {
      toast.success(`${successfulCount}/${requests.length} joins succeeded`);
    }
  }, [joinStatus]);

  useEffect(() => {
    let hasError = false;
    let allSettled = true;
    let anyPendingOrCreating = false;
    let successCount = 0;
    let errorCount = 0;

    if (isLoadingJoinParts) return;

    if (queriesRes.length === 0) {
      setJoinStatus('Idle');
      return;
    }

    const eventStatuses: EventStatus[] = [];

    queriesRes.forEach((res) => {
      const eventStatus = res.data?.event.status;
      eventStatus && eventStatuses.push(eventStatus);
      const failed_1 = eventStatus === EventStatus.Failed;
      const failed_2 = res.isError;

      if (failed_1) {
        setError(res.data?.event.error);
        hasError = true;
        errorCount++;
      } else if (failed_2) {
        const errorStr =
          typeof res.error === 'string'
            ? res.error
            : (res.error as Error).message;
        setError(errorStr);
        hasError = true;
        errorCount++;
      } else if (eventStatus === EventStatus.Settled) {
        successCount++;
      }

      if (eventStatus !== EventStatus.Settled) {
        allSettled = false;
      }

      if (
        eventStatus === EventStatus.Pending ||
        eventStatus === EventStatus.Creating
      ) {
        anyPendingOrCreating = true;
      }
    });

    setSuccessfulCount(successCount);
    setErrorCount(errorCount);
    setEventStatuses(eventStatuses);

    if (hasError && successCount > 0) {
      setJoinStatus('PartialSuccess');
    } else if (hasError) {
      setJoinStatus('Error');
    } else if (allSettled) {
      setJoinStatus('Success');
    } else if (anyPendingOrCreating) {
      setJoinStatus('Joining');
    }
  }, [JSON.stringify(queriesRes)]);

  useEffect(() => {
    console.log('joinStatus', joinStatus);
  }, [joinStatus]);

  return { eventStatuses, joinStatus };
};
